<template>
  <div
    :style="{ '--category-top-position': `${categoriesTopPosition}px` }"
    class="sidebar__games">
    <div
      v-show="isMobile && !activeCategory"
      class="sidebar__search">
      <ui-search-input
        class="header-menu__search"
        size="small"
        placeholder="Search game"
        :value="searchValue"
        @input="searchValue = $event"
        @clear="searchValue = ''" />
    </div>

    <router-link
      v-show="!isMobile"
      class="sidebar__title"
      :to="{ name: 'game__root', params: { namespace: activeGame.routeNamespace } }">
      {{ activeGame.fullTitle }}
    </router-link>

    <div
      ref="wrapper"
      class="sidebar__games-wrapper">
      <div
        class="sidebar__games-list"
        :class="{'sidebar__games-list--hidden': activeCategory}">
        <template v-if="searchValue === ''">
          <span
            v-show="isMobile"
            class="sidebar__title">
            Popular games
          </span>

          <!-- Games list -->
          <div
            v-for="game in popularGames"
            :key="`game-${game.id}`"
            class="sidebar__game"
            @click.stop="gameClickHandler(game)">
            <img
              :src="GameModel.getGameLogo(game.id)"
              height="32"
              width="32"
              :alt="game.fullTitle"
              class="sidebar__game-pic">

            <p
              class="sidebar__game-title">
              {{ game.menuTitle ? game.menuTitle : game.fullTitle }}
            </p>

            <arrowhead-icon class="sidebar__game-arrow" />
          </div>

          <span
            v-show="isMobile"
            class="sidebar__title">
            Other games
          </span>

          <!-- Games list -->
          <div
            v-for="(game, gameIndex) in otherGames"
            :key="`game-${game.id}`"
            class="sidebar__game"
            :class="{'sidebar__game-last': gameIndex === otherGames.length - 1}"
            @click.stop="gameClickHandler(game)">
            <img
              :src="GameModel.getGameLogo(game.id)"
              height="30"
              width="30"
              :alt="game.fullTitle"
              class="sidebar__game-pic">

            <p
              class="sidebar__game-title">
              {{ game.menuTitle ? game.menuTitle : game.fullTitle }}
            </p>

            <arrowhead-icon class="sidebar__game-arrow" />
          </div>
        </template>

        <template v-else>
          <!-- Games list -->
          <div
            v-for="(game, gameIndex) in foundGames"
            :key="`game-${game.id}`"
            class="sidebar__game"
            :class="{
              'sidebar__game-first': gameIndex === 0,
              'sidebar__game-last': gameIndex === foundGames.length - 1
            }"
            @click.stop="gameClickHandler(game)">
            <img
              :src="GameModel.getGameLogo(game.id)"
              height="30"
              width="30"
              :alt="game.fullTitle"
              class="sidebar__game-pic">

            <p
              class="sidebar__game-title">
              {{ game.menuTitle ? game.menuTitle : game.fullTitle }}
            </p>

            <arrowhead-icon class="sidebar__game-arrow" />
          </div>
        </template>
      </div>

      <!-- Categories list -->
      <transition
        name="sidebar__game-categories"
        enter-active-class="sidebar__game-categories--active"
        leave-active-class="sidebar__game-categories--active">
        <div
          v-if="activeCategory"
          ref="categories"
          :key="`categories-${activeCategory.id}`"
          class="sidebar__game-categories scrollbar-default sidebar__game-categories--visible">
          <responsive-image
            :image-data="getImageData(activeCategory.id)"
            fill
            lazyload
            class="sidebar__game-categories-image" />

          <div class="sidebar__game-categories-back">
            <button
              class="button button-round-bordered sidebar__back-button"
              @click="setLocalState('categories', localActiveGameIndex)">
              <img
                class="sidebar__back-button-icon"
                width="12"
                height="8"
                src="/images/icons/arrow-left.svg"
                alt="">
              <span>Back to menu</span>
            </button>
          </div>

          <router-link
            class="sidebar__game-categories-title"
            :to="{ name: 'game__root', params: { namespace: activeCategory.routeNamespace } }">
            {{ activeCategory.fullTitle }}
          </router-link>

          <div
            v-for="(category, index) in getNotHiddenTags(rootTagsByGames[activeCategory.id])"
            :key="category.id"
            :class="{
              'sidebar__category--active': localActiveGameIndex === localActiveGameIndex
                && localActiveCategoryIndex === index,
              'sidebar__category--selected': selectedCategoryId === category.id,
              'sidebar__category--has-children': typeof category._children !== 'undefined',
            }"
            class="sidebar__category">
            <collapse-height
              :ref="`collapse-${index}`"
              class="sidebar__category-collapse">
              <template #toggler>
                <div class="sidebar__category-head">
                  <!--eslint-disable max-len-->
                  <a
                    :href="category._href"
                    class="sidebar__category-title transition--color"
                    @click.stop.prevent="setTagAndActivateCategory(category.id, localActiveGameIndex, category._href, index)">
                    {{ category.title }}
                  </a>
                  <!--eslint-enable max-len-->
                  <div
                    v-if="category._children"
                    class="sidebar__category-plus"
                    @click.stop="toggleEntity(`subcategories-${activeCategory.id}`, index)" />
                </div>
              </template>

              <template #content>
                <div
                  :ref="`subcategories-${activeCategory.id}`"
                  class="sidebar__subcategories-wrapper">
                  <!--eslint-disable max-len-->
                  <div class="sidebar__subcategories">
                    <a
                      v-for="subcategory in category._children"
                      :key="subcategory.id"
                      :href="subcategory._href"
                      :class="{
                        'sidebar__subcategory--long': subcategory._isLongTitle,
                        'sidebar__subcategory--selected': activeTag
                          && (subcategory.id === activeTag.id || subcategory.id === activeTag.parent_id)
                      }"
                      class="sidebar__subcategory transition--color"
                      @click.prevent.stop="setActiveTag(subcategory.id, localActiveGameIndex, subcategory._href)">
                      {{ subcategory.title }}
                    </a>
                  </div>
                  <!--eslint-enable max-len-->
                </div>
              </template>
            </collapse-height>
          </div>
        </div>
      </transition>

      <span ref="intersector" />

      <span
        ref="scrollShadow"
        class="sidebar__gradient-wrapper transition--opacity" />
    </div>
  </div>
</template>

<script>
/* eslint-disable no-unused-vars */
import GameModel from 'Models/GameModel';
import { mapMutations, mapState } from 'vuex';
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import ResponsiveImage from 'Components/ui/Image/ResponsiveImage.vue';
import UiSearchInput from 'Components/ui/Input/UiSearchInput.vue';
import {
  flatTagsById, rootTagsByGames, setFlatTagsById, setRootTagsByGames,
} from 'Root/helpers/tags';
import CollapseHeight from 'Components/ui/CollapseHeight/CollapseHeight.vue';

let scrollIntersectionObserver = null;

export default {
  name: 'MainSidebarGames',

  components: {
    CollapseHeight,
    UiSearchInput,
    ArrowheadIcon,
    ResponsiveImage,
  },

  data() {
    return {
      GameModel,
      localActiveGameIndex: null,
      localActiveCategoryIndex: null,

      categoriesTopPosition: 0,
      searchValue: '',
      rootTagsByGames: rootTagsByGames ?? {},
      flatTagsById: flatTagsById ?? {},
    };
  },

  computed: {
    ...mapState(['activeGame', 'userDevice']),
    ...mapState('products', ['activeTag', 'tags']),

    isMobile() {
      return this.userDevice.innerWidth < 1024;
    },

    activeGames() {
      return GameModel.LIST_ACTIVE;
    },

    activeCategory() {
      return this.activeGames[this.localActiveGameIndex];
    },

    popularGames() {
      return GameModel.LIST_POPULAR;
    },

    otherGames() {
      return GameModel.LIST_OTHER;
    },

    foundGames() {
      const searchValue = this.searchValue.toLowerCase().trim();
      if (!searchValue) {
        return [];
      }

      return [...GameModel.LIST_ACTIVE]
        .filter((game) => {
          const title = `${game.title} ${game.menuTitle} ${game.fullTitle}`;
          return title.toLowerCase().includes(searchValue);
        })
        .sort((a, b) => {
          if ((a.menuTitle || a.fullTitle) > (b.menuTitle || b.fullTitle)) {
            return 1;
          }
          return -1;
        });
    },

    selectedCategoryId() {
      if (!this.activeTag) {
        return null;
      }

      let rootTag = this.flatTagsById[this.activeTag.id];
      let tries = 5;

      while (rootTag?._parent && tries > 0) {
        rootTag = rootTag._parent;
        tries -= 1;
      }

      if (rootTag.parent_id) {
        return rootTag.parent_id;
      }

      return rootTag.id;
    },

    selectedGameIndex() {
      return this.activeGames.findIndex((game) => game.id === this.activeGame.id);
    },

    selectedCategoryIndex() {
      if (this.activeTag) {
        const { id } = this.activeTag;
        return this.rootTagsByGames[this.activeGame.id]
          .findIndex((tag) => tag.id === id || tag.id === this.flatTagsById[id]?.parent_id);
      }

      return null;
    },

    isMobileCategoryOpened() {
      return this.localActiveGameIndex !== null && this.isMobile;
    },
  },

  watch: {
    isMobile: {
      handler(newVal) {
        if (newVal) {
          this.setActiveGame(null);
          this.toggleEntity(`subcategories-${this.activeGame.id}`, this.localActiveCategoryIndex, true);
          return;
        }

        if (this.localActiveGameIndex === this.selectedGameIndex
            && this.localActiveCategoryIndex === this.selectedCategoryIndex) {
          return;
        }
        this.setInitialState();
      },
    },

    'activeGame.id': {
      handler(newVal) {
        if (this.isMobile) {
          this.$emit('hideSidebar');
        }

        this.toggleEntity(`subcategories-${this.activeGame.id}`, this.localActiveCategoryIndex, true);
        const gameIndex = this.activeGames.findIndex((game) => newVal === game.id);
        this.setActiveGame(gameIndex);
        this.setLocalState(`subcategories-${newVal}`, this.selectedCategoryIndex);
      },
    },

    activeTag: {
      handler(newVal) {
        if (this.localActiveCategoryIndex === this.selectedCategoryIndex) {
          return;
        }

        const subcategoryNodes = this.$refs[`subcategories-${this.activeGames[this.localActiveGameIndex]?.id}`];

        if (!subcategoryNodes) {
          return;
        }

        if (newVal === null && this.localActiveCategoryIndex !== null) {
          if (this.localActiveCategoryIndex !== -1) {
            subcategoryNodes[this.localActiveCategoryIndex].style.maxHeight = null;
          }
          this.localActiveCategoryIndex = null;
        }

        if (newVal && !subcategoryNodes?.[this.selectedCategoryIndex]?.style?.maxHeight) {
          // eslint-disable-next-line max-len
          this.toggleEntity(`subcategories-${this.activeGames[this.localActiveGameIndex]?.id}`, this.selectedCategoryIndex);
        }
      },
    },

    isMobileCategoryOpened: {
      handler(newValue) {
        if (newValue) {
          this.categoriesTopPosition = this.$parent.$refs.sidebar.scrollTop;
        }
      },
    },

    tags(val) {
      if (val.length && !this.rootTagsByGames.length) {
        setFlatTagsById(this.tags);
        setRootTagsByGames();
        this.flatTagsById = flatTagsById;
        this.rootTagsByGames = rootTagsByGames;
      }
    },
  },

  mounted() {
    this.setInitialState();
    this.initScrollIntersection();
  },

  beforeDestroy() {
    this.destroyScrollIntersection();
  },

  methods: {
    ...mapMutations('products', ['mutateActiveTag']),

    getImageData(gameId) {
      return {
        default: `${GameModel.getGameBackground(gameId)}-375.jpg`,
        sources: [
          {
            media: '(-webkit-min-device-pixel-ratio: 2)',
            srcset: `${GameModel.getGameBackground(gameId)}-750.avif 1100w
                    ${GameModel.getGameBackground(gameId)}-1100.avif 1400w,
                    ${GameModel.getGameBackground(gameId)}-1400.avif 1920w,
                    ${GameModel.getGameBackground(gameId)}-1920.avif 2800w`,
            type: 'image/avif',
          },
          {
            media: '(-webkit-min-device-pixel-ratio: 1)',
            srcset: `${GameModel.getGameBackground(gameId)}-375.avif 375w,
                     ${GameModel.getGameBackground(gameId)}-750.avif 750w,
                     ${GameModel.getGameBackground(gameId)}-1100.avif 1100w
                     ${GameModel.getGameBackground(gameId)}-1400.avif 1400w,
                     ${GameModel.getGameBackground(gameId)}-1920.avif 1920w,
                     ${GameModel.getGameBackground(gameId)}-2800.avif 2800w`,
            type: 'image/avif',
          },
          {
            media: '(-webkit-min-device-pixel-ratio: 2)',
            srcset: `${GameModel.getGameBackground(gameId)}-750.jpg 1100w
                    ${GameModel.getGameBackground(gameId)}-1100.jpg 1400w,
                    ${GameModel.getGameBackground(gameId)}-1400.jpg 1920w,
                    ${GameModel.getGameBackground(gameId)}-1920.jpg 2800w`,
          },
          {
            media: '(-webkit-min-device-pixel-ratio: 1)',
            srcset: `${GameModel.getGameBackground(gameId)}-375.jpg 375w,
                     ${GameModel.getGameBackground(gameId)}-750.jpg 750w,
                     ${GameModel.getGameBackground(gameId)}-1100.jpg 1100w
                     ${GameModel.getGameBackground(gameId)}-1400.jpg 1400w,
                     ${GameModel.getGameBackground(gameId)}-1920.jpg 1920w,
                     ${GameModel.getGameBackground(gameId)}-2800.jpg 2800w`,
          },
        ],
      };
    },

    setLocalState(entity, index) {
      let stateKey = null;
      if (entity === 'categories') {
        stateKey = 'localActiveGameIndex';
        if (this.localActiveCategoryIndex !== null && this.localActiveCategoryIndex !== -1) {
          // при переходе на другую игру, закрываем открытую категорию
          this.$refs[`collapse-${this.localActiveCategoryIndex}`][0].toggleCollapse(false);
          this.localActiveCategoryIndex = null;
        }
      } else {
        stateKey = 'localActiveCategoryIndex';
      }

      if (!stateKey) {
        return;
      }

      if (this[stateKey] !== index) {
        this[stateKey] = index;
      } else if (entity !== 'categories' || this.isMobile) {
        this[stateKey] = null;
      }
    },

    gameClickHandler(game) {
      const gameIndex = this.activeGames.findIndex((activeGame) => activeGame.id === game.id);

      if (this.localActiveGameIndex !== gameIndex) {
        this.setLocalState('categories', gameIndex);
      }

      this.$emit('hideSidebar');

      if (this.$route.name !== 'game__root' || game.routeNamespace !== this.$route.params.namespace) {
        this.mutateActiveTag(null);
        this.$router.push({ name: 'game__root', params: { namespace: game.routeNamespace } });
      }
    },

    toggleEntity(entity, index, close = false) {
      if (close) {
        this.$refs[`collapse-${index}`]?.[0]?.toggleCollapse(false);
      }

      if (this.localActiveCategoryIndex !== index) {
        // закрываем ранее открытую категорию
        if (this.localActiveCategoryIndex !== null && this.localActiveCategoryIndex !== -1) {
          this.$refs[`collapse-${this.localActiveCategoryIndex}`][0].toggleCollapse(false);
        }

        this.$refs[`collapse-${index}`]?.[0]?.toggleCollapse(true);
        this.setLocalState(entity, index);
      } else if (this.localActiveCategoryIndex === index) {
        this.$refs[`collapse-${index}`]?.[0]?.toggleCollapse(false);
        this.localActiveCategoryIndex = null;
      }
    },

    setTagAndActivateCategory(tagId, gameIndex, link, categoryIndex) {
      if (this.$route.path === link) {
        return;
      }

      if (this.localActiveCategoryIndex !== categoryIndex) {
        this.toggleEntity(`subcategories-${this.activeGames[gameIndex].id}`, categoryIndex);
      }

      this.setActiveTag(tagId, gameIndex, link);
    },

    setActiveTag(tagId, gameIndex, link) {
      this.setActiveGame(gameIndex);
      this.mutateActiveTag(tagId);
      if (this.$route.path !== link) {
        this.$emit('hideSidebar');
        this.$router.push(link); // todo SKY-1577 исследовать тройной вызов функции в профайлере, в логах - 1
      }
    },

    setActiveGame(gameIndex) {
      this.localActiveGameIndex = gameIndex;
    },

    initScrollIntersection() {
      scrollIntersectionObserver = new IntersectionObserver(([entry]) => {
        this.$refs.scrollShadow.style.opacity = entry.isIntersecting ? 0 : 1;
      }, {
        root: this.$refs.wrapper,
        threshold: 1,
        rootMargin: '0px',
      });

      scrollIntersectionObserver.observe(this.$refs.intersector);
    },

    destroyScrollIntersection() {
      if (!scrollIntersectionObserver) {
        return;
      }
      scrollIntersectionObserver.disconnect();
      scrollIntersectionObserver = null;
    },

    setInitialState() {
      if (this.isMobile) {
        return;
      }

      this.setActiveGame(this.selectedGameIndex);

      if (this.selectedCategoryIndex !== null) {
        this.$nextTick(() => {
          this.toggleEntity(`subcategories-${this.activeCategory.id}`, this.selectedCategoryIndex);
        });
      }
    },

    getNotHiddenTags(tags) {
      return tags.filter((tag) => !tag.no_index);
    },
  },
};
</script>

<style lang="scss">
  .sidebar__games {
    -webkit-tap-highlight-color: transparent;
  }

  .sidebar__games-wrapper {
    position: relative;
    background-color: $color--basic-menu;
  }

  .sidebar__search {
    padding: 16px 20px 0;
    background-color: $color--basic-menu;
  }

  .sidebar__title {
    display: block;
    text-transform: uppercase;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.1em;
    color: $color--basic-dark-gray;
    padding: 24px 20px 12px;
    background-color: $color--basic-menu;
  }

  // Game
  .sidebar__game {
    position: relative;
    padding: 8px 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    background-color: $color--basic-menu;

    &:last-child:before {
      display: none;
    }

    &:before {
      content: "";
      display: block;
      width: calc(100% - 40px);
      height: 1px;
      position: absolute;
      bottom: 0;
      left: 20px;
      background-color: #{rgba($color--basic-dark-gray, 0.55)};
    }
  }

  .sidebar__game-first {
    padding-top: 32px;
  }

  .sidebar__game-last {
    padding-bottom: 24px;

    &:before {
      display: none;
    }
  }

  .sidebar__game-pic {
    filter: $filter--light-gray-to-white;
  }

  .sidebar__game-title {
    font-weight: 500;
    font-size: 15px;
    margin-left: 16px;
    position: relative;
  }

  .sidebar__game-arrow {
    --width: 8px !important;

    position: relative;
    width: 20px !important;
    padding: 16px;
    margin: -6px 4px -6px auto;
    transform: rotate(-90deg);
  }

  // Category
  .sidebar__game-categories-image,
  .sidebar__game-categories-back,
  .sidebar__game-categories-title {
    display: none;
  }

  .sidebar__category {
    padding: 16px 0;
    cursor: pointer;
    position: relative;
    z-index: 2;
    border-bottom: 1px solid #{rgba($color--basic-dark-gray, 0.55)};

    &:last-child {
      border: none;
    }

    &--active {
      .sidebar__category-plus {
        &:before {
          transform: translate(-50%, -50%) rotate(90deg);
        }

        &:after {
          opacity: 0;
        }
      }
    }
  }

  .sidebar__category-head {
    display: flex;
    justify-content: space-between;
  }

  .sidebar__category-title {
    display: block;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.2;
    color: $color--basic-white;
    flex: 1;

    &:hover {
      color: $color--basic-green;
    }
  }

  .sidebar__category-plus {
    position: relative;
    width: 20px;
    padding: 10px 20px;
    margin: -10px 0px;

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      background-color: $color--basic-white;
      transform: translate(-50%, -50%);
      transition-duration: 0.2s;
      transition-timing-function: ease-out;
    }

    &:before {
      height: 15px;
      width: 2px;
      transition-property: transform, background-color;
    }

    &:after {
      height: 2px;
      width: 15px;
      transition-property: opacity, background-color;
    }
  }

  .sidebar__subcategories-wrapper {
    transition: max-height 0.15s ease-out;
  }

  .sidebar__category-collapse {
    --content-padding-top: 0 !important;
  }

  .sidebar__subcategory {
    display: block;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 500;
    color: $color--basic-light-gray;

    &--selected {
      color: $color--basic-light-gray;
    }

    &:hover {
      color: $color--basic-green;
    }
  }

  .sidebar__category--selected {
    .sidebar__category-title {
      color: $color--basic-light-gray;
    }

    .sidebar__category-plus {
      &:before,
      &:after {
        background-color: $color--basic-light-gray;
      }
    }
  }

  @media screen and #{$screen-less-than-1024} {
    .sidebar__game-categories {
      position: relative;
      width: 100%;
      min-height: calc(100vh - #{$size-mobile-header-height});
      top: 0;
      left: 0;
      z-index: 5;
      padding: 16px 20px 16px;
      transition: transform .15s ease-out;
      background-color: $color--basic-menu;
      overflow: auto;

      &--active {
        position: absolute;
        top: 0;
        transform: translateX(-100%);
      }
    }

    .sidebar__game-categories-image {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
      mix-blend-mode: luminosity;
      opacity: 0.55;
      max-height: 450px;
      height: 100%;

      /deep/ .responsive-image__img {
        object-position: top center;
      }

      &:after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        background-image: linear-gradient(0deg, rgb(33, 48, 65) 0%, rgba(71, 92, 127, 0) 100%);
        height: 40%;
      }
    }

    .sidebar__game-categories-back {
      display: block;
      margin-bottom: 24px;
      position: relative;
      z-index: 10;
    }

    .sidebar__back-button {
      display: inline-flex;
      align-items: center;
      border-radius: 5px;
      padding: 12px 14px;
      height: 40px;
    }

    .sidebar__back-button-icon {
      margin-right: 8px;
    }

    .sidebar__game-categories-title {
      display: block;
      margin-bottom: 4px;
      position: relative;
      z-index: 10;
      text-transform: uppercase;
      color: $color--basic-dark-gray;
      font-size: 13px;
      letter-spacing: 0.1em;
      font-weight: 500;
    }

    .sidebar__subcategories {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: .75em 1em;
      padding-top: 8px;
    }

    .sidebar__subcategory {
      margin-bottom: 0;

      &--long {
        grid-column: 1 / -1;
      }
    }

    .sidebar__games-list--hidden {
      height: 0;
      overflow: hidden;
    }
  }

  @media screen and #{$screen-more-or-1024} {
    .sidebar__games {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding-right: 8px;
    }

    .sidebar__title {
      padding: 24px 20px 16px $padding-pc-sidebar;
      background-color: transparent;
    }

    .sidebar__games-wrapper {
      min-height: unset;
      background-color: unset;
      flex: 1 1 auto;
      overflow: auto;
    }

    .sidebar__game {
      display: none;
    }

    .sidebar__game-categories {
      display: none;

      &--active {
        display: block;
        background-color: $color--basic-black;
      }

      &--visible {
        display: block;
        background-color: $color--basic-black;
      }
    }

    .sidebar__category {
      padding: 0 20px 2px $padding-pc-sidebar;
      border: none;
    }

    .sidebar__category--active.sidebar__category--has-children {
      margin-bottom: 20px;
    }

    .sidebar__category-head {
      margin-bottom: 10px;
    }

    .sidebar__category-title {
      font-size: 15px;
      line-height: 18px;
    }

    .sidebar__category-plus {
      margin: -10px -10px;
    }

    .sidebar__subcategories-wrapper {
      margin-left: 20px;
    }

    .sidebar__subcategory {
      font-size: 15px;
      margin-bottom: 10px;

      &:not(.sidebar__subcategory--selected) {
        color: $color--basic-white;
      }
    }

    .sidebar__gradient-wrapper {
      display: block;
      position: sticky;
      bottom: 0;
      opacity: 1;
      z-index: 5;

      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 40px;
        background-image: linear-gradient(0deg, $color--basic-black 0%, rgba($color--basic-black, 0) 100%);
        display: block;
        pointer-events: none;
        z-index: 2;
      }
    }
  }

  @media #{$device--mouse} {
    .sidebar__category-head:hover {
      .sidebar__category-title {
        color: $color--basic-green !important; // перебиваем active стейт
      }

      .sidebar__category-plus {
        &:before,
        &:after {
          background-color: $color--basic-green !important; // перебиваем active стейт
        }
      }
    }

    .sidebar__subcategory:hover {
      color: $color--basic-green !important; // перебиваем active стейт
    }
  }
</style>
